import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../services/firebase';
import type { Claim, ClaimStatus } from '../types';

interface UseClaimStatusOptions {
  claimId: string | null;
}

interface UseClaimStatusResult {
  status: ClaimStatus | null;
  claim: Claim | null;
  error: Error | null;
}

/**
 * claim のステータスをリアルタイム監視する hook
 * onSnapshot を使用してFirestoreの変更を即座に検知
 */
export function useClaimStatus({ claimId }: UseClaimStatusOptions): UseClaimStatusResult {
  const [status, setStatus] = useState<ClaimStatus | null>(null);
  const [claim, setClaim] = useState<Claim | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!claimId) {
      setStatus(null);
      setClaim(null);
      return;
    }

    const claimRef = doc(db, 'claims', claimId);

    const unsubscribe = onSnapshot(
      claimRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const claimData: Claim = {
            id: snapshot.id,
            uid: data.uid,
            raceId: data.raceId,
            raceAlias: data.raceAlias,
            raceName: data.raceName,
            raceDate: data.raceDate,
            bib: data.bib,
            startTime: data.startTime,
            time: data.time,
            netTime: data.netTime,
            status: data.status,
            chain: data.chain,
            contractAddress: data.contractAddress,
            destination: data.destination,
            videoWorkerIndex: data.videoWorkerIndex ?? null,
            mintWorkerIndex: data.mintWorkerIndex ?? null,
            mintWalletAddress: data.mintWalletAddress ?? null,
            fileName: data.fileName,
            sourceFileName: data.sourceFileName,
            txHash: data.txHash,
            txFee: data.txFee ?? null,
            tokenId: data.tokenId,
            tokenName: data.tokenName,
            mediaUri: data.mediaUri ?? null,
            hasIntegrated: data.hasIntegrated ?? false,
            mint_context: data.mint_context,
            videoRetryCount: data.videoRetryCount ?? 0,
            mintRetryCount: data.mintRetryCount ?? 0,
            lastError: data.lastError ?? null,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            startedAt: data.startedAt ?? null,
            videoCompletedAt: data.videoCompletedAt ?? null,
            completedAt: data.completedAt,
            failedAt: data.failedAt,
            deletedAt: data.deletedAt,
            randomSeed: data.randomSeed ?? 0,
          };
          setClaim(claimData);
          setStatus(data.status as ClaimStatus);
        }
      },
      (err) => {
        console.error('Claim snapshot error:', err);
        setError(err instanceof Error ? err : new Error('ステータス監視に失敗しました'));
      }
    );

    return () => unsubscribe();
  }, [claimId]);

  return { status, claim, error };
}
